import { ListOf } from 'app/redux/models/common/ListOf'

import { Security } from './Security.js'

/**
 * @class
 * @typedef {Array.<Security>} SecurityList
 */
export class SecurityList extends ListOf(Security) {
  /**
   * @param {number} id
   * @return {?Security}
   */
  get(id) {
    return this.find((security) => security.id === id) ?? null
  }

  /**
   * @param {Security} security
   * @return {SecurityList}
   */
  add(security) {
    const newSecurities = new SecurityList(...this)
    newSecurities.push(security)

    return newSecurities
  }

  /**
   * @param {Security} security
   * @return {SecurityList}
   */
  replace(security) {
    const newSecurities = new SecurityList(...this)
    const currentSecurityIndex = newSecurities.findIndex((collectionSecurity) => collectionSecurity.id === security.id)

    if (currentSecurityIndex < 0) {
      return newSecurities
    }

    newSecurities[currentSecurityIndex] = security

    return newSecurities
  }

  /**
   * @param {Security} security
   * @return {SecurityList}
   */
  replaceOrAdd(security) {
    if (this.findIndex((collectionSecurity) => collectionSecurity.id === security.id) > -1) {
      return this.replace(security)
    }

    return this.add(security)
  }

  /**
   * @param {SecurityList} securitiesListToMerge
   * @return {SecurityList | Array<api.SecurityShortDTO | api.SecurityDetailedDTO>}
   */
  merge(securitiesListToMerge) {
    if (securitiesListToMerge.length === 0) {
      return this
    }

    if (this.length === 0) {
      return new SecurityList(...securitiesListToMerge)
    }

    const result = new SecurityList()
    const idToIndexMap = new Map()

    // First pass: add all from securitiesListToMerge, assuming that merge list is bigger
    for (const security of securitiesListToMerge) {
      idToIndexMap.set(security.id, result.length)
      result.push(security)
    }

    // Second pass: merge or add from this
    for (const securityFromThis of this) {
      const existingIndex = idToIndexMap.get(securityFromThis.id)

      if (existingIndex !== undefined) {
        result[existingIndex] = securityFromThis.merge(result[existingIndex])
        continue
      }

      // Add new security
      result.push(securityFromThis)
    }

    return result
  }

  /**
   * @return {SecurityList}
   */
  getVisibleInUniverse() {
    return this.filter((security) => security.is_visible_in_universe)
  }
}
